import { useState } from 'react';
import {
  Row, Col, Card, CardBody, CardTitle, CardText, Button, Input,
  Form, FormGroup, Label, Alert, Badge,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { RelativeTime } from '../../components/RelativeTime';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { useDrivers, useUpdateDriver, useRemoveDriverFromTenant } from './useDrivers';

/**
 * Drivers-View: Card-Grid pro Driver mit Health-Indikator + Toggles + Config-Form.
 * Spec-Quelle: 01_apis/01_core.api.valitype.net/ADMIN/views.md → #/drivers
 */
export function DriversPage() {
  const query = useDrivers();
  const canWrite = useHasPermission({ perms: ['drivers.write'] });

  if (query.isLoading) {
    return (
      <div>
        <PageHeader title="Drivers" />
        <QueryLoadingState />
      </div>
    );
  }
  if (query.isError) {
    return (
      <div>
        <PageHeader title="Drivers" />
        <QueryErrorState error={query.error} onRetry={() => query.refetch()} />
      </div>
    );
  }

  const drivers = query.data?.drivers ?? [];

  return (
    <div>
      <PageHeader
        title="Drivers"
        subtitle="Provisioning-Driver pro Brand · GET /v1/admin/drivers"
      />
      <Row className="g-3">
        {drivers.map((d, idx) => (
          <Col md="6" lg="6" key={d.key ?? d.driver_id ?? d.id ?? `driver-${idx}`}>
            <DriverCard driver={d} canWrite={canWrite} />
          </Col>
        ))}
      </Row>
    </div>
  );
}

function DriverCard({ driver, canWrite }) {
  const [showConfig, setShowConfig] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(false);
  const update = useUpdateDriver();
  const remove = useRemoveDriverFromTenant();

  const healthState = computeHealth(driver);

  const onGlobalToggle = () => {
    update.mutate({ key: driver.key, patch: { globalEnabled: !driver.globalEnabled } });
  };
  const onTenantToggle = () => {
    update.mutate({ key: driver.key, patch: { tenantEnabled: !driver.tenantEnabled } });
  };

  return (
    <>
      <Card className="h-100">
        <CardBody>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <div>
              <CardTitle tag="h5" className="mb-1">{driver.name}</CardTitle>
              <code className="small text-muted">{driver.key}</code>
            </div>
            <HealthBadge state={healthState} />
          </div>

          <CardText className="text-muted small">{driver.description}</CardText>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <ToggleBlock
              id={`global-${driver.key}`}
              label="Global aktiviert"
              hint="System-weit"
              checked={driver.globalEnabled}
              onChange={onGlobalToggle}
              disabled={!canWrite || update.isPending}
            />
            <ToggleBlock
              id={`tenant-${driver.key}`}
              label="In dieser Brand"
              hint="Tenant-spezifisch"
              checked={driver.tenantEnabled}
              onChange={onTenantToggle}
              disabled={!canWrite || update.isPending || !driver.globalEnabled}
            />
          </div>

          {driver.lastSuccessAt && (
            <div className="small text-muted">
              Letzter Erfolg: <RelativeTime iso={driver.lastSuccessAt} />
            </div>
          )}
          {driver.lastFailureAt && (
            <Alert color="danger" className="small mt-2 mb-0 py-2">
              <div>
                Letzter Fehler: <RelativeTime iso={driver.lastFailureAt} />
              </div>
              {driver.lastError && <div className="mt-1"><code>{driver.lastError}</code></div>}
            </Alert>
          )}

          <HasPermission perms={['drivers.write']}>
            <div className="d-flex gap-2 mt-3">
              <Button
                color="primary"
                outline
                size="sm"
                onClick={() => setShowConfig(!showConfig)}
              >
                {showConfig ? 'Config schließen' : 'Tenant-Config bearbeiten'}
              </Button>
              {driver.tenantEnabled && (
                <Button color="danger" outline size="sm" onClick={() => setPendingRemove(true)}>
                  Aus Tenant entfernen
                </Button>
              )}
            </div>

            {showConfig && (
              <DriverConfigForm
                driver={driver}
                onSave={(config) => {
                  update.mutate({ key: driver.key, patch: { config } });
                }}
                busy={update.isPending}
              />
            )}
          </HasPermission>
        </CardBody>
      </Card>

      <ConfirmDialog
        isOpen={pendingRemove}
        title={`${driver.name} aus dieser Brand entfernen?`}
        body={
          <>
            Driver-Config + State werden für diese Brand gelöscht. Die globale
            Driver-Definition bleibt; in anderen Brands läuft er weiter.
            <br />
            <strong>Aktive Services</strong> dieser Brand die diesen Driver brauchen,
            failen beim nächsten Provisioning-Trigger.
          </>
        }
        confirmLabel="Entfernen"
        confirmColor="danger"
        busy={remove.isPending}
        onConfirm={async () => {
          await remove.mutateAsync({ key: driver.key });
          setPendingRemove(false);
        }}
        onCancel={() => setPendingRemove(false)}
      />
    </>
  );
}

function DriverConfigForm({ driver, onSave, busy }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(driver.configSchema.map((f) => [f.key, driver.config?.[f.key] ?? ''])),
  );

  const onSubmit = (e) => {
    e.preventDefault();
    onSave(form);
  };

  return (
    <Form onSubmit={onSubmit} className="mt-3 pt-3 border-top">
      {driver.configSchema.map((f) => (
        <FormGroup key={f.key}>
          <Label className="small fw-semibold">{f.label}</Label>
          <Input
            type={f.type === 'secret' ? 'password' : f.type === 'url' ? 'url' : 'text'}
            value={form[f.key] ?? ''}
            onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
            placeholder={f.type === 'secret' && driver.config?.[f.key] ? `••••${String(driver.config[f.key]).slice(-4)}` : ''}
            bsSize="sm"
            disabled={busy}
            autoComplete="new-password"
          />
        </FormGroup>
      ))}
      <Button type="submit" color="primary" size="sm" disabled={busy}>
        {busy ? 'Speichern…' : 'Config speichern'}
      </Button>
    </Form>
  );
}

function ToggleBlock({ id, label, hint, checked, onChange, disabled }) {
  return (
    <div>
      <div className="form-check form-switch">
        <Input
          type="switch"
          role="switch"
          id={id}
          checked={checked}
          onChange={onChange}
          disabled={disabled}
        />
        <Label check for={id} className="small fw-semibold ms-1">
          {label}
        </Label>
      </div>
      <div className="text-muted" style={{ fontSize: 11 }}>{hint}</div>
    </div>
  );
}

function HealthBadge({ state }) {
  if (state === 'healthy') return <Badge color="success" pill>healthy</Badge>;
  if (state === 'degraded') return <Badge color="warning" pill>degraded</Badge>;
  if (state === 'failing') return <Badge color="danger" pill>failing</Badge>;
  if (state === 'disabled') return <Badge color="secondary" pill>disabled</Badge>;
  return <Badge color="secondary" pill>unknown</Badge>;
}

function computeHealth(d) {
  if (!d.globalEnabled || !d.tenantEnabled) return 'disabled';
  if (!d.lastSuccessAt && !d.lastFailureAt) return 'unknown';
  if (d.lastFailureAt && !d.lastSuccessAt) return 'failing';
  if (d.lastFailureAt && d.lastSuccessAt) {
    return new Date(d.lastFailureAt) > new Date(d.lastSuccessAt) ? 'degraded' : 'healthy';
  }
  return 'healthy';
}
